import { useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Link2, ExternalLink, Plus, Trash2, Pencil } from "lucide-react";

const CATEGORIES = ["DGT", "Legislación", "Seguridad Vial", "Formación", "Vídeos", "Herramientas", "Recursos Natur", "Tienda", "Otros"];

export default function Links() {
  const { user } = useAuth();
  const isMatriz = user.role === "SUPER_ADMIN";
  const [links, setLinks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", category: "DGT", url: "", icon: "", status: "active", pin: "" });

  const load = () => api.get("/links").then((r) => setLinks(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const { pin, ...body } = form;
      const headers = { "X-Master-Pin": pin };
      if (editing) await api.put(`/links/${editing}`, body, { headers });
      else await api.post("/links", body, { headers });
      toast.success(editing ? "Enlace actualizado" : "Enlace creado");
      setShowForm(false); setEditing(null);
      setForm({ name: "", description: "", category: "DGT", url: "", icon: "", status: "active", pin: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (id) => {
    const pin = window.prompt("PIN Maestro para eliminar:");
    if (!pin) return;
    try {
      await api.delete(`/links/${id}`, { headers: { "X-Master-Pin": pin } });
      toast.success("Enlace eliminado"); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const byCategory = links.reduce((acc, l) => { (acc[l.category] = acc[l.category] || []).push(l); return acc; }, {});

  return (
    <div className="fade-up" data-testid="links-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Enlaces</h1>
          <p className="text-neutral-500 mt-1">Biblioteca centralizada de enlaces útiles, autorizada por la matriz Natur.</p>
        </div>
        {isMatriz && (
          <Button data-testid="link-add-btn" onClick={() => { setShowForm(!showForm); setEditing(null); }}
                  className="rounded-full bg-orange-600 hover:bg-orange-700 text-white">
            <Plus size={15} className="mr-1.5" /> Añadir enlace
          </Button>
        )}
      </div>

      {isMatriz && showForm && (
        <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6 mb-8 grid md:grid-cols-3 gap-3" data-testid="link-form">
          <Input data-testid="link-name-input" placeholder="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input data-testid="link-url-input" placeholder="URL" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
          <select data-testid="link-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="h-10 rounded-md border border-neutral-300 px-3 text-sm">
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <Input placeholder="Descripción breve" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="md:col-span-2" />
          <Input placeholder="URL imagen/icono (opcional)" value={form.icon || ""} onChange={(e) => setForm({ ...form, icon: e.target.value })} />
          <select data-testid="link-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="h-10 rounded-md border border-neutral-300 px-3 text-sm">
            <option value="active">Activo</option>
            <option value="inactive">Inactivo</option>
          </select>
          <Input data-testid="link-pin-input" placeholder="PIN Maestro" type="password" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value })} />
          <Button data-testid="link-save-btn" onClick={save} className="rounded-full bg-neutral-900 text-white">
            {editing ? "Guardar cambios" : "Crear enlace"}
          </Button>
        </div>
      )}

      {Object.entries(byCategory).map(([cat, items]) => (
        <div key={cat} className="mb-8">
          <h2 className="text-xs tracking-[0.2em] uppercase font-semibold text-neutral-500 mb-3">{cat}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((l) => (
              <div key={l.id} data-testid="link-card"
                   className={`bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-5 hover:shadow-lg transition-shadow ${l.status !== "active" ? "opacity-50" : ""}`}>
                <div className="flex items-start justify-between">
                  <div className="w-9 h-9 rounded-lg bg-orange-100 text-orange-700 flex items-center justify-center shrink-0">
                    {l.icon ? <img src={l.icon} alt="" className="w-5 h-5 object-contain" /> : <Link2 size={17} />}
                  </div>
                  {isMatriz && (
                    <div className="flex gap-1">
                      <button data-testid="link-edit-btn" onClick={() => { setEditing(l.id); setShowForm(true); setForm({ name: l.name, description: l.description, category: l.category, url: l.url, icon: l.icon || "", status: l.status, pin: "" }); window.scrollTo(0, 0); }}
                              className="p-1.5 text-neutral-400 hover:text-orange-600"><Pencil size={14} /></button>
                      <button data-testid="link-delete-btn" onClick={() => del(l.id)} className="p-1.5 text-neutral-400 hover:text-red-600"><Trash2 size={14} /></button>
                    </div>
                  )}
                </div>
                <h3 className="font-semibold mt-3">{l.name}</h3>
                <p className="text-sm text-neutral-500 mt-1 line-clamp-2">{l.description}</p>
                {isMatriz && (
                  <p className="text-[10px] text-neutral-400 mt-2">
                    {l.status === "active" ? "Activo" : "Inactivo"} · {l.created_by} · {fmtDate(l.created_at)} · {l.history?.length || 1} cambios
                  </p>
                )}
                <a href={l.url} target="_blank" rel="noreferrer" data-testid="link-open"
                   className="mt-3 inline-flex items-center gap-1 text-sm text-orange-700 hover:underline">
                  Visitar <ExternalLink size={13} />
                </a>
              </div>
            ))}
          </div>
        </div>
      ))}
      {links.length === 0 && <p className="text-sm text-neutral-500">Todavía no hay enlaces publicados.</p>}
    </div>
  );
}
